import React, { useState } from "react";
import axios from "axios";
import { useApp } from "@/context/AppContext";
import SEO from "@/components/SEO";
import { paramsToState } from "@/utils/urlState";

const defaultData = {
  method: "cnap",
  current_age: 35,
  retirement_age: 65,
  career_start_age: 23,
  education_years: 0,
  monthly_gross_salary: 5500,
  past_salary_growth: 2.0,
  future_salary_growth: 1.5,
  tax_class: "1",
  children: 0,
};

const fmt = (v) =>
  new Intl.NumberFormat("fr-LU", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

/**
 * Embed page — iframe-friendly minimal calculator.
 * Designed for external sites/blogs to embed via <iframe src="/embed">.
 */
const EmbedPage = () => {
  const { t, API } = useApp();
  const [data, setData] = useState(() => paramsToState(window.location.search, defaultData));
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setData({ ...data, [k]: e.target.value });

  const calc = async () => {
    setLoading(true);
    try {
      const toNum = (v) => {
        if (v === "" || v == null) return 0;
        const n = parseFloat(String(v).replace(",", "."));
        return Number.isFinite(n) ? n : 0;
      };
      const payload = {
        method: data.method,
        current_age: Math.trunc(toNum(data.current_age)),
        retirement_age: Math.trunc(toNum(data.retirement_age)),
        career_start_age: Math.trunc(toNum(data.career_start_age)),
        education_years: Math.trunc(toNum(data.education_years || 0)),
        monthly_gross_salary: toNum(data.monthly_gross_salary),
        past_salary_growth: toNum(data.past_salary_growth || 1.5),
        future_salary_growth: toNum(data.future_salary_growth || 1.5),
        tax_class: data.tax_class,
        children: Math.trunc(toNum(data.children)),
      };
      const res = await axios.post(`${API}/pension/calculate`, payload);
      setResult(res.data);
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full bg-white border border-slate-300 px-2 py-1.5 text-sm font-mono focus:border-slate-900 focus:ring-0";
  const lbl = "text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1 block";

  return (
    <div className="bg-white min-h-screen p-4" data-testid="embed-page">
      <SEO title="Embed Calculator" description="Luxembourg pension mini-calculator (embeddable)." path="/embed" />
      <div className="max-w-3xl mx-auto border border-slate-300 p-5">
        <div className="flex items-baseline justify-between border-b border-slate-200 pb-3 mb-4">
          <div className="font-serif text-lg text-slate-900">Lux·Pension <span className="text-blue-700">·</span> Mini</div>
          <a href="https://pensioncheck.online" target="_blank" rel="noopener noreferrer" data-testid="embed-credit"
            className="text-[10px] font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900">
            Powered by lux-pension ↗
          </a>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <label className="block"><span className={lbl}>{t.form.currentAge}</span>
            <input type="number" className={input} value={data.current_age} onChange={upd("current_age")} data-testid="embed-age" />
          </label>
          <label className="block"><span className={lbl}>{t.form.retirementAge}</span>
            <input type="number" className={input} value={data.retirement_age} onChange={upd("retirement_age")} data-testid="embed-retire" />
          </label>
          <label className="block"><span className={lbl}>{t.form.careerStartAge}</span>
            <input type="number" className={input} value={data.career_start_age} onChange={upd("career_start_age")} data-testid="embed-start" />
          </label>
          <label className="block"><span className={lbl}>€/mo</span>
            <input type="number" className={input} value={data.monthly_gross_salary} onChange={upd("monthly_gross_salary")} data-testid="embed-salary" />
          </label>
          <label className="block"><span className={lbl}>{t.form.taxClass}</span>
            <select className={input} value={data.tax_class} onChange={upd("tax_class")} data-testid="embed-class">
              <option value="1">1</option><option value="1a">1a</option><option value="2">2</option>
            </select>
          </label>
          <label className="block"><span className={lbl}>{t.form.children}</span>
            <input type="number" className={input} value={data.children} onChange={upd("children")} data-testid="embed-children" />
          </label>
          <button onClick={calc} disabled={loading} data-testid="embed-calc"
            className="col-span-2 md:col-span-2 px-4 py-2 bg-slate-900 text-white text-[11px] font-mono uppercase tracking-widest hover:bg-slate-800 disabled:opacity-50">
            {loading ? "…" : t.form.calculate}
          </button>
        </div>

        {result && (
          <div className="grid grid-cols-3 gap-4 mt-5 pt-4 border-t border-slate-200" data-testid="embed-result">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{t.result.gross}</div>
              <div className="font-mono tabular text-xl text-slate-900 mt-1">{fmt(result.gross_monthly_pension)}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{t.result.net}</div>
              <div className="font-mono tabular text-xl text-blue-700 mt-1">{fmt(result.net_monthly_pension)}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{t.result.replacement}</div>
              <div className="font-mono tabular text-xl text-slate-900 mt-1">{result.replacement_rate}%</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmbedPage;
